import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { TrendUp, ShoppingCart, Package, Wallet, CalendarBlank, Receipt } from "@phosphor-icons/react";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid={`dash-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}><Icon size={16} weight="duotone" /></div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-slate-900 font-mono-num tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); };
const PRESETS = [
  { label: "Today", from: () => todayStr(), to: () => todayStr() },
  { label: "Last 7 days", from: () => daysAgo(6), to: () => todayStr() },
  { label: "Last 30 days", from: () => daysAgo(29), to: () => todayStr() },
  { label: "This month", from: () => todayStr().slice(0, 8) + "01", to: () => todayStr() },
];

export default function StoreDashboard() {
  const { storeId } = useParams();
  const [d, setD] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    api.get(`/store/${storeId}/dashboard?${params.toString()}`).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [storeId, from, to]);

  if (!d) return <Shell><PageHeader title="Store Dashboard" /><div className="p-8 text-slate-400">Loading…</div></Shell>;
  const range = d.range;

  return (
    <Shell>
      <PageHeader title="Store Dashboard" subtitle="Realtime sales and inventory KPIs." testId="store-dash-header" />
      <div className="p-8 space-y-6">
        <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="dash-date-filter">
          <CalendarBlank size={16} className="text-slate-500" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">From</span>
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="dash-from" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">To</span>
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="dash-to" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          <div className="inline-flex bg-slate-50 border border-slate-200 rounded-md p-1">
            {PRESETS.map((p) => (
              <button key={p.label} onClick={() => { setFrom(p.from()); setTo(p.to()); }} data-testid={`dash-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`} className="px-3 h-8 text-xs font-semibold rounded-sm text-slate-700 hover:bg-white hover:shadow-sm">{p.label}</button>
            ))}
          </div>
          {(from || to) && <button onClick={() => { setFrom(""); setTo(""); }} data-testid="dash-clear" className="h-10 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Clear</button>}
          {loading && <span className="text-xs text-slate-400 ml-auto">Updating…</span>}
        </div>

        {range && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-range-stats">
            <Stat icon={Wallet} label="Period Sales" value={inr(range.total)} sub={`${range.from} → ${range.to}`} accent="bg-emerald-500 text-white" />
            <Stat icon={Receipt} label="Period Bills" value={range.count.toLocaleString("en-IN")} sub="non-cancelled invoices" accent="bg-slate-900 text-white" />
            <Stat icon={ShoppingCart} label="Items Sold" value={Number(range.items || 0).toLocaleString("en-IN")} sub="units across all bills" accent="bg-amber-50 text-amber-700" />
            <Stat icon={TrendUp} label="Avg Bill" value={inr(range.avg_bill)} sub="period sales ÷ bills" accent="bg-slate-100 text-slate-700" />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Wallet} label="Today Sales" value={inr(d.today.total)} sub={`${d.today.count} bills`} accent="bg-emerald-50 text-emerald-700" />
          <Stat icon={TrendUp} label="This Week" value={inr(d.week.total)} sub={`${d.week.count} bills`} accent="bg-amber-50 text-amber-700" />
          <Stat icon={ShoppingCart} label="This Month" value={inr(d.month.total)} sub={`${d.month.count} bills`} accent="bg-slate-900 text-white" />
          <Stat icon={Package} label="Inventory" value={d.inventory.units.toLocaleString("en-IN")} sub={`${d.inventory.skus} SKUs`} accent="bg-slate-100 text-slate-700" />
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-display text-lg font-bold mb-4" data-testid="trend-title">{range ? `Sales Trend — ${range.from} to ${range.to}` : "Sales Trend — 14 days"}</h2>
          {d.trend.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">{range ? "No sales in the selected period." : "No sales yet. Start billing to see the trend here."}</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={d.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v, n) => n === "total" ? [inr(v), "Sales"] : [v, "Bills"]} />
                  <Bar dataKey="total" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
